/**
 * Statusplakette.
 *
 * Kapitel 44 verbietet Statusinformation allein über Farbe. Diese Komponente
 * setzt das durch: Sie zeigt **immer** Farbe, Symbol und Text. Es gibt bewusst
 * keine Möglichkeit, das Symbol oder den Text weglassen zu lassen — ein
 * optionales Feld wäre der erste Ort, an dem die Regel wieder aufweicht.
 *
 * Die Farbzuordnung folgt Kapitel 11.3.
 */
import type { ReactNode } from "react";

import { IconAlert, IconCheck, IconClock, IconShield, IconUpload } from "@/components/icons";
import { cn } from "@/lib/cn";

/** Farbtöne nach Kapitel 11.3. */
export type StatusTone = "grey" | "red" | "yellow" | "blue" | "orange" | "green" | "violet";

const TONES: Record<StatusTone, string> = {
  grey: "bg-status-grey-bg text-status-grey-fg",
  red: "bg-status-red-bg text-status-red-fg",
  yellow: "bg-status-yellow-bg text-status-yellow-fg",
  blue: "bg-status-blue-bg text-status-blue-fg",
  orange: "bg-status-orange-bg text-status-orange-fg",
  green: "bg-status-green-bg text-status-green-fg",
  violet: "bg-status-violet-bg text-status-violet-fg",
};

/**
 * Standardsymbol je Farbton. Die Zuordnung ist die inhaltliche Bedeutung aus
 * Kapitel 11.3, nicht Dekoration: rot = kritisch, gelb = Entscheidung offen,
 * blau = geplant, orange = in Bearbeitung, grün = erledigt,
 * violett = Nachbeobachtung, grau = nicht bewertet.
 */
const TONE_ICONS: Record<StatusTone, ReactNode> = {
  grey: <IconClock />,
  red: <IconAlert />,
  yellow: <IconAlert />,
  blue: <IconClock />,
  orange: <IconUpload />,
  green: <IconCheck />,
  violet: <IconShield />,
};

export interface StatusPillProps {
  tone: StatusTone;
  /** Sichtbarer Text. Pflicht — siehe Kapitel 44. */
  children: ReactNode;
  /** Ersetzt das Standardsymbol des Farbtons, entfernt es aber nie. */
  icon?: ReactNode;
  size?: "sm" | "md";
  className?: string;
}

export function StatusPill({ tone, children, icon, size = "md", className }: StatusPillProps) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-card font-medium",
        // Ein feiner Innenrand in Schriftfarbe: Bei ausgeschalteten Farben
        // (Kontrastmodus, Graustufendruck) bleibt die Plakette als Element erkennbar.
        "ring-1 ring-inset ring-current/25",
        size === "sm" ? "px-1.5 py-0.5 text-[0.75rem]" : "px-2 py-1 text-[0.8125rem]",
        TONES[tone],
        className,
      )}
    >
      <span className="shrink-0 text-[0.9em] leading-none">{icon ?? TONE_ICONS[tone]}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}
